import {
	useMutation,
	UseMutationResult,
	useQuery,
	useQueryClient,
	UseQueryResult,
} from '@tanstack/react-query'

import { PaginatedResponse } from '@core/api/dtos/common/types'
import { CreateLocationRequestDTO } from '@core/api/dtos/request/location/create'
import { ShareLocationRequestDTO } from '@core/api/dtos/request/location/share'
import { UpdateLocationRequestDTO } from '@core/api/dtos/request/location/update'
import { Location } from '@core/api/entities/location'
import { LocationMapper } from '@core/api/mappers/locationMapper'
import { locationService } from '@core/api/services/locationService'
import { useAccountConfig } from '@core/hooks/useAccountConfig'

import { DEFAULT_PARAMS } from './common'
import { ContactUpdateRequestDTO } from '../api/dtos/request/location/contactUpdate'
import { DeleteLocationRequestDTO } from '../api/dtos/request/location/delete'

/**
 * Get locations
 */
export const useLocations = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	PaginatedResponse<Location>,
	Error
> => {
	const accountConfig = useAccountConfig(config, true)

	const QUERY_KEY = [
		'locations',
		{
			...accountConfig?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			locationService
				.paginateLocations(accountConfig)
				.then(({ data: response }) => {
					return {
						data: LocationMapper.toEntities(response.data),
						meta: response.meta,
					}
				}),
		...options,
	})
}

/**
 * Get location
 */
export const useLocation = ({
	playerId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Location, Error> => {
	const QUERY_KEY = [
		'location',
		playerId,
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			locationService
				.getLocation(playerId, config)
				.then(({ data: response }) => {
					return LocationMapper.toEntity(response.data)
				}),
		...options,
	})
}

/**
 * Create location
 */
export const useCreateLocation = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Location,
	Error,
	CreateLocationRequestDTO,
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<Location, Error, CreateLocationRequestDTO, any>({
		mutationFn: (data: CreateLocationRequestDTO) =>
			locationService
				.createLocation(data, config)
				.then(({ data: response }) => {
					return LocationMapper.toEntity(response.data)
				}),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['locations'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Update location
 */
export const usePatchLocation = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Location,
	Error,
	{ locationId: number; data: UpdateLocationRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		Location,
		Error,
		{ locationId: number; data: UpdateLocationRequestDTO },
		any
	>({
		mutationFn: ({
			locationId,
			data,
		}: {
			locationId: number
			data: UpdateLocationRequestDTO
		}) =>
			locationService
				.patchLocation(locationId, data, config)
				.then(({ data: response }) => {
					return LocationMapper.toEntity(response.data)
				}),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['locations'] })
			queryClient.invalidateQueries({ queryKey: ['location'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Update location contact information
 */
export const useUpdateContactLocation = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Location,
	Error,
	{ locationId: number; data: ContactUpdateRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		Location,
		Error,
		{ locationId: number; data: ContactUpdateRequestDTO },
		any
	>({
		mutationFn: ({
			locationId,
			data,
		}: {
			locationId: number
			data: ContactUpdateRequestDTO
		}) =>
			locationService
				.patchContactLocation(locationId, data, config)
				.then(({ data: response }) => {
					return LocationMapper.toEntity(response.data)
				}),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['locations'] })
			queryClient.invalidateQueries({ queryKey: ['location'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Delete location
 */
export const useDeleteLocation = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ locationId: number; data: DeleteLocationRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ locationId: number; data: DeleteLocationRequestDTO },
		any
	>({
		mutationFn: ({ locationId }) =>
			locationService.deleteLocation(locationId, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['locations'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Share location link
 */
export const useShareLocationLink = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ locationId: number; data: ShareLocationRequestDTO },
	any
> => {
	return useMutation<
		void,
		Error,
		{ locationId: number; data: ShareLocationRequestDTO },
		any
	>({
		mutationFn: ({ locationId, data }) =>
			locationService
				.shareLocationLink(locationId, data, config)
				.then(() => {}),
		...options,
	})
}
